import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Window, TextField, Button, Popup, PopupBody, ValidationIndicator, LoadingOverlay, type ValidationStatus } from '@warmancer/ui';
import { authApi, ApiClientError, getErrorMessage, DISCORD_URL, type RegisterResponse } from '../api/client';
import { useFingerprint } from '../hooks/useFingerprint';
import styles from './AuthPages.module.css';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

export function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Username availability status
  const { getFingerprint } = useFingerprint();

  const [usernameStatus, setUsernameStatus] = useState<ValidationStatus>('idle');
  const [usernameMessage, setUsernameMessage] = useState<string>('');

  // Debounced username availability check
  useEffect(() => {
    // No validation until user starts typing
    if (username.length === 0) {
      setUsernameStatus('idle');
      setUsernameMessage('');
      return;
    }

    // Show invalid for too short
    if (username.length < 3) {
      setUsernameStatus('invalid');
      setUsernameMessage('Minimum 3 characters');
      return;
    }

    // Basic format validation first
    if (!/^[a-zA-Z0-9]+$/.test(username)) {
      setUsernameStatus('invalid');
      setUsernameMessage('Only letters and numbers allowed');
      return;
    }

    if (username.length > 16) {
      setUsernameStatus('invalid');
      setUsernameMessage('Maximum 16 characters');
      return;
    }

    setUsernameStatus('checking');
    setUsernameMessage('');

    const timeout = setTimeout(async () => {
      try {
        const result = await authApi.checkUsername(username);
        if (result.available) {
          setUsernameStatus('valid');
          setUsernameMessage('Name available');
        } else {
          setUsernameStatus('invalid');
          setUsernameMessage(result.error || 'Name not available');
        }
      } catch (err) {
        // Network errors must NOT block form submission.
        // Set status to idle so the user can still submit — the server
        // will validate the username during registration anyway.
        setUsernameStatus('idle');
        setUsernameMessage('Could not verify — will check on submit');
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [username]);

  // Real-time email validation
  const emailStatus = useMemo((): ValidationStatus => {
    if (!email) return 'idle';
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'valid' : 'invalid';
  }, [email]);

  // Real-time password validation
  const passwordStatus = useMemo((): ValidationStatus => {
    if (!password) return 'idle';
    return password.length >= 7 ? 'valid' : 'invalid';
  }, [password]);

  // Real-time password match validation
  const passwordMatchStatus = useMemo((): ValidationStatus => {
    if (!confirmPassword) return 'idle';
    return password === confirmPassword ? 'valid' : 'invalid';
  }, [password, confirmPassword]);

  const registerMutation = useMutation<RegisterResponse, Error>({
    mutationFn: async () => {
      const fp = await getFingerprint();
      return authApi.register({
        username,
        email,
        password,
        referral: referralCode || undefined,
        turnstile_token: turnstileToken!,
        fp_request_id: fp.requestId,
      });
    },
    onSuccess: (data: RegisterResponse) => {
      if (data.verification_required) {
        setSuccess(true);
      } else {
        navigate('/login');
      }
    },
    onError: (error: Error) => {
      // Reset Turnstile on error
      turnstileRef.current?.reset();
      setTurnstileToken(null);

      const message = getErrorMessage(error);
      if (error instanceof ApiClientError && error.field) {
        setErrors({ [error.field]: message });
      } else {
        setErrors({ form: message });
      }
    },
  });

  const resendMutation = useMutation({
    mutationFn: async () => {
      return authApi.resendVerificationPublic({ email });
    },
    onSuccess: () => {
      setResendSuccess(true);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Client-side validation
    const newErrors: Record<string, string> = {};

    if (!username.trim()) {
      newErrors.username = 'Name is required';
    } else if (username.length < 3 || username.length > 16) {
      newErrors.username = 'Name must be 3-16 characters';
    } else if (!/^[a-zA-Z0-9]+$/.test(username)) {
      newErrors.username = 'Name can only contain letters and numbers';
    } else if (usernameStatus === 'invalid') {
      newErrors.username = usernameMessage || 'Name not available';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 7) {
      newErrors.password = 'Password must be at least 7 characters';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!turnstileToken) {
      newErrors.form = 'Please complete the CAPTCHA verification';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    registerMutation.mutate();
  };

  const clearFormError = () => {
    setErrors((prev) => {
      const { form, ...rest } = prev;
      return rest;
    });
  };

  return (
    <>
      <Window title="Create Account" maxWidth={400} className={styles.window}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <TextField
            name="username"
            label="Name"
            labelHint="3-16 letters/numbers"
            value={username}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setUsername(e.target.value);
              if (errors.username) setErrors((prev) => { const { username: _, ...rest } = prev; return rest; });
            }}
            error={!!errors.username}
            autoComplete="username"
            autoFocus
            maxLength={16}
            tabIndex={1}
            rightElement={<ValidationIndicator status={errors.username ? 'invalid' : usernameStatus} message={errors.username || usernameMessage} />}
          />

          <TextField
            name="email"
            type="email"
            label="Email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEmail(e.target.value);
              if (errors.email) setErrors((prev) => { const { email: _, ...rest } = prev; return rest; });
            }}
            error={!!errors.email}
            autoComplete="email"
            tabIndex={2}
            rightElement={<ValidationIndicator status={errors.email ? 'invalid' : emailStatus} message={errors.email || (emailStatus === 'invalid' ? 'Please enter a valid email' : '')} />}
          />

          <TextField
            name="password"
            type="password"
            label="Password"
            labelHint="7+ characters"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setPassword(e.target.value);
              if (errors.password) setErrors((prev) => { const { password: _, ...rest } = prev; return rest; });
            }}
            error={!!errors.password}
            autoComplete="new-password"
            tabIndex={3}
            rightElement={<ValidationIndicator status={errors.password ? 'invalid' : passwordStatus} message={errors.password || (passwordStatus === 'invalid' ? 'Minimum 7 characters' : '')} />}
          />

          <TextField
            name="confirmPassword"
            type="password"
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setConfirmPassword(e.target.value);
              if (errors.confirmPassword) setErrors((prev) => { const { confirmPassword: _, ...rest } = prev; return rest; });
            }}
            error={!!errors.confirmPassword}
            autoComplete="new-password"
            tabIndex={4}
            rightElement={<ValidationIndicator status={errors.confirmPassword ? 'invalid' : passwordMatchStatus} message={errors.confirmPassword || (passwordMatchStatus === 'invalid' ? 'Passwords do not match' : '')} />}
          />

          <div className={styles.turnstileWrapper}>
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={setTurnstileToken}
              onError={() => setTurnstileToken(null)}
              onExpire={() => setTurnstileToken(null)}
              options={{
                theme: 'dark',
                size: 'normal',
              }}
            />
          </div>

          <Button
            type="submit"
            disabled={registerMutation.isPending || !turnstileToken}
            tabIndex={5}
          >
            Submit
          </Button>

          <Button
            type="button"
            variant="cancel"
            onClick={() => navigate('/login')}
            tabIndex={6}
          >
            Cancel
          </Button>
        </form>
      </Window>

      <LoadingOverlay
        visible={registerMutation.isPending || resendMutation.isPending}
        message={resendMutation.isPending ? 'Sending verification email...' : 'Creating your account...'}
        timeout={10000}
      />

      <Popup
        open={!!errors.form}
        title="Registration Failed"
        variant="error"
      >
        <PopupBody
          note={<>Join us on <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord</a> for help</>}
          buttons={<Button onClick={clearFormError}>Try Again</Button>}
        >
          {errors.form?.split('\n').map((line, i) => <p key={i}>{line}</p>)}
        </PopupBody>
      </Popup>

      <Popup
        open={success}
        title="Check Your Email"
        variant="success"
      >
        <PopupBody
          note={<>Email sent from contact@warmancer.com<br />Join us on <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">Discord</a> for help</>}
          buttons={<>
            <Button
              onClick={() => resendMutation.mutate()}
              disabled={resendMutation.isPending || resendSuccess}
            >
              {resendSuccess ? 'Email Sent!' : 'Resend Email'}
            </Button>
            <Button onClick={() => navigate('/login')}>
              Return to Login
            </Button>
          </>}
        >
          <p>We've sent a verification link to <strong>{email}</strong>.</p>
          <p>Please check your inbox <strong>and spam folder</strong>.</p>
          <p>You have <strong>24 hours</strong> to verify your email. After that, your account will be removed and you can register again.</p>
        </PopupBody>
      </Popup>
    </>
  );
}
